import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { CellModel, ColumnSource, RequestFilters, RowSource, Renderer } from "@omegagrid/grid";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { GroupTreeAdapter } from "@reporting/rp-mf-accounting"
import { AccountingGroup, User } from "@reporting/rp-types/core";
import { AccessRole } from "@reporting/rp-types/users";
import { CallerContext } from "../types";

const PAGE_SIZE = 500;

const loginNameRenderer: Renderer = (div: HTMLDivElement, cell: CellModel) => {
	const value = cell.value as string ?? '';
	if (value.indexOf('/') > -1) {
		const [domain, login] = value.split('/');
		div.innerHTML = `<span style="color: var(--og-accent-color)">${domain}</span>/${login}`;
	} else {
		div.textContent = value;
	}
	return div;
}

export class UserGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private g: number;

	constructor(
		private group: AccountingGroup = null,
		private sa: boolean = false
	) {
		super();
		this.g = group?.id_group || null;
	}


	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			content: (() => {
				const controls = [];
				if (rp.userProvider.isGroupAdmin()) {
					controls.push({
						action: 'delete',
						title: msg('Delete'),
						icon: 'trash',
						color: 'red'
					});
				}
				controls.push({
					action: 'editUser',
					title: msg('Edit'),
					icon: 'pen',
					color: 'gray'
				});
				if (rp.userProvider.isGroupAdmin()) {
					controls.push({
						action: 'settings',
						title: msg('Settings'),
						icon: 'gear',
						color: 'gray'
					});
				}
				if (!this.sa) {
					controls.push({
						action: 'access',
						title: msg('Access'),
						icon: 'key',
						color: 'yellow'
					});
				}
				controls.push({
					action: 'impersonate',
					title: msg('Impersonate'),
					icon: 'arrow-right-to-bracket',
					color: 'blue'
				});
				return controls;
			})(),
			e: false,
			w: (() => {
				let btns = 2;
				if (rp.userProvider.isGroupAdmin()) btns += 2; // delete + settings
				if (!this.sa) btns++;
				return 5 + 20 * btns;
			})(),
			filter: false
		}, {
			data: 'user_name',
			header: '#',
			t: 't',
			e: false,
			w: 130
		}, {
			data: 'domain_login_name',
			header: msg('User name'),
			t: 't',
			e: true,
			w: 200,
			renderer: loginNameRenderer
		}];
		if (this.group) {
			if (this.group.sso || this.group.sso_saml2 || this.group.sso_oidc) {
				this._columns.push({
					data: 'login_name_2',
					header: `${msg('User name')} (alt)`,
					t: 't',
					e: true,
					w: 200
				});
			}
			const roles = await rp.ms('users').get<AccessRole[]>(
				'accessRoles',
				{ fields: ['id_access_role', 'role'], ...(this.sa && this.g ? { g: this.g } : {}) }
			);
			this._columns.push({
				data: 'user_groups',
				header: `${msg('Groups')} (${msg('users')})`,
				t: 'm',
				e: true,
				w: 180,
				content: roles.map(role => ({
					k: role.id_access_role.toString(),
					v: role.role
				})),
			});
		}
		if (this.sa) {
			this._columns.push({
				data: 'accounting_groups',
				header: `${msg('Groups')} (${msg('companies')})`,
				t: 'm',
				e: true,
				w: 180,
				adapter: new GroupTreeAdapter({ selectMode: 'multiple', autoClose: false, allowEmpty: false }),
			});
		}
		this._columns.push({
			data: 'email',
			header: msg('Email'),
			t: 't',
			e: true,
			w: 200
		}, {
			data: 'phone',
			header: msg('Phone'),
			t: 't',
			e: true,
			w: 160
		}, {
			data: 'last_login',
			header: msg('Last login'),
			t: 'dt',
			e: false,
			w: 145
		}, {
			data: 'disabled',
			header: msg('Disabled'),
			t: 'b',
			e: true,
			w: 105
		}, {
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true
		});
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]>{
		const users = await rp.ms('users').get<User[]>(this.sa ? `allUsers/${this.g}` : 'groupUsers', Object.assign(this.createRequestParams(filters, {
			count: PAGE_SIZE
		}), { withGroups: this.sa }));
		return users.map(data => ({ data: {
			...data,
			user_groups: { v: data.user_groups, dv: data.display_user_groups },
			accounting_groups: { v: data.accounting_groups, dv: data.display_accounting_groups },
		}}));
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: PAGE_SIZE,
			refreshAfterSave: true,
			toolbar: {
				new: {enabled: false},
			},
			customToolbar: this.group ? [{
				type: 'button',
				id: 'newUser',
				icon: { icon: 'plus' },
				text: msg('New user'),
				color: ColorTypes.Blue,
			}] : [],
		});
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign(
			{_state: row.state, _index: row.index, _super: this.sa, g: this.g},
			row.data
		));
		data.forEach(row => {
			row.positive = !row.negative;
			this._columns.filter(c => ['s', 'm'].includes(c.t)).forEach(col => {
					const value = row[col.data];
					if (Array.isArray(value) && value.every(Array.isArray)) {
						row[col.data] = value.map(([first]) => first);
					}
				});
		});
		await rp.ms('users').post('users', data);
	}

	refresh() {
		this.editor.refresh();
	}

	async onAction(action: string, cell: CellModel) {
		const data = cell?.row.data;
		switch (action) {
			case 'newUser':
				rp.host.openModalSidebar({id: `users-userForm-${this.sa ? 'super' : 'admin' as CallerContext}-${this.g}`, caption: msg('New user')});
				break;
			case 'editUser':
				const userName = data.user_name;
				rp.host.openModalSidebar({id: `users-userForm-${this.sa ? 'super' : 'admin' as CallerContext}-${this.g || 0}-${userName}`, caption: msg('User settings')});
				break;
			case 'settings':
				rp.host.open(
					`users-settings-user-${data.user_name}`,
					{ title: `${msg('Settings')}: ${data.login_name || data.user_name}` }
				);
				break;
			case 'access':
				rp.host.open(
					`users-userAccessContainer-u-${data.user_name}-g-${this.g}`,
					{ title: `${msg('Access')}: ${data.login_name || data.user_name}` }
				);
				break;
			case 'impersonate':
				const command = await rp.host.openMenu({
					content: 'yes_no',
					text: `${msg('Do you want to impersonate user')} ${data.login_name || data.user_name}?`
				});
				if (command?.id != 'Y') return;
				try {
					await rp.auth.client.impersonate(data.user_name, rp.accountingProvider.a);
					rp.userProvider.impersonator = rp.userProvider.id;
					rp.userProvider.impersonator_dv = rp.userProvider.user?.login_name;
					rp.router.clearHostState();
					rp.create();
				} catch (e: any) {
					rp.host.bucket?.showAlert({ title: 'Error', content: e.data?.message || e.message, type: 'danger' });
				}
				break;
		}
	}

}
